import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SnackbarKey, useSnackbar } from 'notistack';

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';

import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { LUX_PLUS_SCHEDULE } from '~/consts/schedule';

import DateTimeWidget from '../../../Common/Elements/DateTimeWidget';

interface Props {
  label: string;
  type: string;
  schedule: App.Schedule;
  isModified: boolean;
  onChange: (schedule: App.Schedule, type: string) => void;
  onDelete: (id: number) => void;
  disabled: boolean;
  isDeleting?: boolean;
  warning?: string;
}

const ScheduleTableRow = memo((props: Props) => {
  const {
    label,
    type,
    schedule,
    isModified,
    onChange,
    onDelete,
    disabled,
    isDeleting: isExternallyDeleting = false,
    warning,
  } = props;

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [isInternallyDeleting, setInternallyDeleting] = useState(false);
  const [isUndoWindowOpen, setIsUndoWindowOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const isDeleting = isInternallyDeleting || isExternallyDeleting;
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const snackbarKeyRef = useRef<SnackbarKey | undefined>(undefined);

  // Always-current ref: updated synchronously on every render so callbacks
  // never go stale without needing `schedule` in their dependency arrays.
  const scheduleRef = useRef(schedule);
  scheduleRef.current = schedule;

  useEffect(() => {
    return () => {
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    };
  }, []);

  // Shared by the snackbar's own Undo action and the inline Undo button rendered in place of the
  // Delete button — the snackbar is easy to miss (e.g. behind an open dialog), so the inline
  // control is the primary affordance and the snackbar is a secondary one.
  const cancelDelete = useCallback(() => {
    if (undoTimerRef.current) {
      clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }
    if (snackbarKeyRef.current !== undefined) {
      closeSnackbar(snackbarKeyRef.current);
      snackbarKeyRef.current = undefined;
    }
    setIsUndoWindowOpen(false);
    setInternallyDeleting(false);
  }, [closeSnackbar]);

  const confirmDelete = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setInternallyDeleting(true);
    setIsUndoWindowOpen(true);

    snackbarKeyRef.current = enqueueSnackbar(`Schedule for ${label} deleted`, {
      variant: 'info',
      action: () => (
        <Button size="small" color="inherit" onClick={cancelDelete}>
          Undo
        </Button>
      ),
      autoHideDuration: 5000,
    });

    undoTimerRef.current = setTimeout(() => {
      setIsUndoWindowOpen(false);
      closeSnackbar(snackbarKeyRef.current);
      snackbarKeyRef.current = undefined;
      onDelete(scheduleRef.current.id);
    }, 5000);
  }, [label, enqueueSnackbar, closeSnackbar, onDelete, cancelDelete]);

  // Stable callbacks — depend on onChange/type but NOT on the schedule object.
  // scheduleRef.current always has the latest schedule at call time.
  const onStartChange = useCallback(
    (start: string) => {
      onChange({ ...scheduleRef.current, start }, type);
    },
    [onChange, type],
  );

  const onEndChange = useCallback(
    (end: string) => {
      onChange({ ...scheduleRef.current, end }, type);
    },
    [onChange, type],
  );

  const toggleRestrictPurchasesFlag = useCallback(
    (event) => {
      onChange({ ...scheduleRef.current, restrict_purchase_to_lux_plus_members: event.target.checked }, type);
    },
    [onChange, type],
  );

  // Memoized date range — only re-renders when start/end/disabled change.
  // Clicking "Restrict all" only changes restrict_purchase_to_lux_plus_members,
  // so the expensive DateTimeWidgets are skipped entirely.
  // LuxPlus rows get a wider format + minWidth: their dialog (CampaignScheduleBrandGroupModal)
  // is the main place these get edited, and the default compact width clips full date+time.
  const isLuxPlus = type === LUX_PLUS_SCHEDULE;
  const dateRangeCell = useMemo(
    () => (
      <TableCell>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={isLuxPlus ? { minWidth: 220 } : undefined}>
            <DateTimeWidget
              disabled={disabled}
              value={schedule.start}
              onChange={onStartChange}
              format={isLuxPlus ? 'DD/MM/YYYY hh:mm a' : undefined}
            />
          </Box>
          <ArrowRightAltIcon />
          <Box sx={isLuxPlus ? { minWidth: 220 } : undefined}>
            <DateTimeWidget
              disabled={disabled}
              value={schedule.end}
              onChange={onEndChange}
              format={isLuxPlus ? 'DD/MM/YYYY hh:mm a' : undefined}
            />
          </Box>
        </Stack>
      </TableCell>
    ),
    [schedule.start, schedule.end, disabled, onStartChange, onEndChange, isLuxPlus],
  );

  return (
    <TableRow>
      <TableCell>
        {label}
        {warning && (
          <Typography variant="caption" color="warning.main" display="block" mt={0.5}>
            {warning}
          </Typography>
        )}
      </TableCell>
      {dateRangeCell}
      {isLuxPlus && (
        <TableCell>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!schedule.restrict_purchase_to_lux_plus_members}
                onChange={toggleRestrictPurchasesFlag}
              />
            }
            label="Restrict purchase to LuxPlus+ members"
          />
        </TableCell>
      )}
      <TableCell>
        <PermissionedComponent>
          {isUndoWindowOpen ? (
            <Stack alignItems="center" direction="row" spacing={1}>
              <Typography color="text.secondary" variant="body2">
                Deleting…
              </Typography>
              <Button size="small" onClick={cancelDelete}>
                Undo
              </Button>
            </Stack>
          ) : (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setIsDeleteDialogOpen(true)}
              disabled={isDeleting || disabled}
              aria-label={`Delete schedule for ${label}`}
              startIcon={<DeleteIcon />}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          )}
        </PermissionedComponent>
      </TableCell>
      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
        <DialogTitle>Delete schedule for {label}?</DialogTitle>
        <DialogContent>
          <Typography>This schedule will be removed. You will have 5 seconds to undo.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            To avoid accidental removals, delete schedules from the bottom and work up.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </TableRow>
  );
});

ScheduleTableRow.displayName = 'ScheduleTableRow';

export default ScheduleTableRow;
